import React from "react"

import { ChevronLeft, ChevronRight } from "lucide-react"

import { cn } from "@/utils/cn"

interface HeaderCycleNavigationProps {
  displayIndex: number | null
  hasNext: boolean
  hasPrevious: boolean
  isLoading?: boolean
  navAriaLabel: string
  nextAriaLabel: string
  onNext: () => void
  onPortalSlotMount?: (element: HTMLDivElement | null) => void
  onPrevious: () => void
  previousAriaLabel: string
  totalItems: number
}

export const HeaderCycleNavigation: React.FC<HeaderCycleNavigationProps> = ({
  displayIndex,
  hasNext,
  hasPrevious,
  isLoading = false,
  navAriaLabel,
  nextAriaLabel,
  onNext,
  onPortalSlotMount,
  onPrevious,
  previousAriaLabel,
  totalItems,
}) => {
  if (isLoading) {
    return (
      <div className="flex items-center gap-1.5 shrink-0">
        <div className="h-4 w-16 animate-pulse rounded bg-surface-2" />
      </div>
    )
  }

  if (displayIndex == null || totalItems === 0) {
    return null
  }

  return (
    <div className="flex items-center gap-2">
      <nav
        className="flex items-center gap-1.5 shrink-0 text-sm"
        aria-label={navAriaLabel}
      >
        <button
          type="button"
          onClick={onPrevious}
          disabled={!hasPrevious}
          aria-label={previousAriaLabel}
          className={cn(
            "p-0.5 rounded transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-coval-info-border",
            hasPrevious
              ? "text-foreground hover:bg-surface-1-hover cursor-pointer"
              : "text-coval-text-muted cursor-default",
          )}
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <span className="flex items-center gap-0.5">
          <span className="text-foreground font-medium">{displayIndex}</span>
          <span className="text-coval-text-muted">/ {totalItems}</span>
        </span>
        <button
          type="button"
          onClick={onNext}
          disabled={!hasNext}
          aria-label={nextAriaLabel}
          className={cn(
            "p-0.5 rounded transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-coval-info-border",
            hasNext
              ? "text-foreground hover:bg-surface-1-hover cursor-pointer"
              : "text-coval-text-muted cursor-default",
          )}
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </nav>
      {onPortalSlotMount ? (
        <div ref={onPortalSlotMount} className="flex items-center gap-1" />
      ) : null}
    </div>
  )
}
